import React, { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";
import type { serviceStruct } from "../interfaces/serviceInterface";
import { serviceStateEnum } from "../interfaces/servicesTypes";
import { NetworkFetch } from "@/network/networkFetch";
import { apiRoutes } from "@/network/apiRoutes";
import { useUser } from "./UserContext";
import { useToastContext } from "./ToastContext";

interface ServiceContextType {
  services: serviceStruct[];
  loading: boolean;
  error: string | null;
  selectedServiceId: string | null;
  setSelectedServiceId: (id: string | null) => void;
  selectedService: serviceStruct | null;
  refreshServices: () => Promise<void>;
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

export const ServiceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const { error: showError } = useToastContext();

  const [services, setServices] = useState<serviceStruct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);

  const fetchServices = async () => {
    if (!user?._id) return;

    setLoading(true);
    setError(null);
    try {
      const fetchedServices = await NetworkFetch(apiRoutes.getServices, { customerId: user._id });
      setServices(fetchedServices || []);

      // Auto-select first service if none is selected
      if (!selectedServiceId && fetchedServices?.length > 0) {
        const firstService = fetchedServices.find((service) => service.serviceState !== serviceStateEnum.RETIRED);
        if (!firstService) {
          return;
        }
        const id = String(firstService?._id ?? firstService?.documentNumber ?? "0");
        setSelectedServiceId(id);
      }
    } catch (err) {
      const errorMessage = "Error al cargar los servicios. Por favor, intenta nuevamente.";
      setError(errorMessage);
      showError(errorMessage);
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?._id) {
      fetchServices();
    }
  }, [user?._id]);

  const selectedService = services.find(
    (s) => String(s?._id ?? s?.documentNumber) === selectedServiceId
  ) || null;

  const value: ServiceContextType = {
    services,
    loading,
    error,
    selectedServiceId,
    setSelectedServiceId,
    selectedService,
    refreshServices: fetchServices,
  };

  return <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>;
};

export const useServices = (): ServiceContextType => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error("useServices must be used within a ServiceProvider");
  }
  return context;
};
